import React, { useState } from 'react';
import {
  ShieldCheckIcon,
  BellIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const SettingsView = ({ userAddress }) => {
  const [settings, setSettings] = useState({
    notifications: {
      bridgeComplete: true,
      avatarUpdates: true,
      securityAlerts: true,
      marketingEmails: false
    },
    privacy: {
      showAssets: true,
      showActivity: true,
      allowAnalytics: false
    },
    bridge: {
      defaultMetaverse: 'ready_player_me',
      autoBridge: false,
      slippageTolerance: 1.0
    },
    security: {
      requireApproval: true,
      maxBridgeValue: 1000,
      trustedMetaverses: ['ready_player_me', 'oma3']
    }
  });

  const handleSettingChange = (category, setting, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));
  };

  const saveSettings = () => {
    // In production, this would save to backend
    localStorage.setItem('galaxia-bridge-settings', JSON.stringify(settings));
    alert('Settings saved successfully!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="galaxia-card p-6">
        <h1 className="text-2xl font-bold text-galaxia">Settings</h1>
        <p className="text-galaxia-muted mt-1">
          Manage your bridge preferences and security settings
        </p>
      </div>

      {/* Notifications */}
      <div className="galaxia-card p-6">
        <div className="flex items-center mb-4">
          <BellIcon className="h-6 w-6 text-galaxia-muted mr-3" />
          <h3 className="text-lg font-semibold text-galaxia">Notifications</h3>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-galaxia">Bridge Complete</p>
              <p className="text-sm text-galaxia-muted">Get notified when bridge operations complete</p>
            </div>
            <button
              onClick={() => handleSettingChange('notifications', 'bridgeComplete', !settings.notifications.bridgeComplete)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                settings.notifications.bridgeComplete ? 'galaxia-toggle-on' : 'galaxia-toggle'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.notifications.bridgeComplete ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-galaxia">Avatar Updates</p>
              <p className="text-sm text-galaxia-muted">Get notified about avatar changes across metaverses</p>
            </div>
            <button
              onClick={() => handleSettingChange('notifications', 'avatarUpdates', !settings.notifications.avatarUpdates)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                settings.notifications.avatarUpdates ? 'galaxia-toggle-on' : 'galaxia-toggle'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.notifications.avatarUpdates ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-galaxia">Security Alerts</p>
              <p className="text-sm text-galaxia-muted">Get notified about security-related events</p>
            </div>
            <button
              onClick={() => handleSettingChange('notifications', 'securityAlerts', !settings.notifications.securityAlerts)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                settings.notifications.securityAlerts ? 'galaxia-toggle-on' : 'galaxia-toggle'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.notifications.securityAlerts ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Bridge Settings */}
      <div className="galaxia-card p-6">
        <div className="flex items-center mb-4">
          <ArrowRightIcon className="h-6 w-6 text-galaxia-muted mr-3" />
          <h3 className="text-lg font-semibold text-galaxia">Bridge Settings</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-galaxia mb-2">
              Default Target Metaverse
            </label>
            <select
              value={settings.bridge.defaultMetaverse}
              onChange={(e) => handleSettingChange('bridge', 'defaultMetaverse', e.target.value)}
              className="galaxia-input galaxia-select w-full"
            >
              <option value="ready_player_me">Ready Player Me</option>
              <option value="oma3">OMA3</option>
              <option value="galaxia">Galaxia Native</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-galaxia">Auto-bridge Assets</p>
              <p className="text-sm text-galaxia-muted">Automatically bridge compatible assets</p>
            </div>
            <button
              onClick={() => handleSettingChange('bridge', 'autoBridge', !settings.bridge.autoBridge)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                settings.bridge.autoBridge ? 'galaxia-toggle-on' : 'galaxia-toggle'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.bridge.autoBridge ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-galaxia mb-2">
              Slippage Tolerance (%)
            </label>
            <input
              type="number"
              value={settings.bridge.slippageTolerance}
              onChange={(e) => handleSettingChange('bridge', 'slippageTolerance', parseFloat(e.target.value))}
              className="galaxia-input galaxia-select w-full"
              min="0.1"
              max="5"
              step="0.1"
            />
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="galaxia-card p-6">
        <div className="flex items-center mb-4">
          <ShieldCheckIcon className="h-6 w-6 text-galaxia-muted mr-3" />
          <h3 className="text-lg font-semibold text-galaxia">Security</h3>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-galaxia">Require Manual Approval</p>
              <p className="text-sm text-galaxia-muted">Require approval for all bridge operations</p>
            </div>
            <button
              onClick={() => handleSettingChange('security', 'requireApproval', !settings.security.requireApproval)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                settings.security.requireApproval ? 'galaxia-toggle-on' : 'galaxia-toggle'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.security.requireApproval ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-galaxia mb-2">
              Maximum Bridge Value (GXAs)
            </label>
            <input
              type="number"
              value={settings.security.maxBridgeValue}
              onChange={(e) => handleSettingChange('security', 'maxBridgeValue', parseInt(e.target.value))}
              className="galaxia-input galaxia-select w-full"
              min="1"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          className="galaxia-btn-primary px-6 py-3 text-sm font-medium"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default SettingsView;
